import type { ComputedRef, Ref, WritableComputedRef } from 'vue'
import { computed, ref, watch } from 'vue'
import { debounce, isArray } from 'remeda'
import { cloneDeep } from '@/helpers/clone-deep'

type ClearFilterFactory = {
    <TKey extends string, TEntry extends string>(
        filters: WritableComputedRef<Record<TKey, TEntry[] | null>>,
        field: TKey,
        value: TEntry
    ): () => void

    <TKey extends string, TEntry = undefined>(
        filters: WritableComputedRef<Record<TKey, TEntry | null | undefined>>,
        field: TKey,
        value?: TEntry
    ): () => void
}

export const getClearFilter: ClearFilterFactory =
    <TKey extends string, TEntry extends string>(
        filters: WritableComputedRef<Record<TKey, TEntry[] | null>>,
        field: TKey,
        value: TEntry
    ) =>
    () => {
        filters.value = {
            ...filters.value,
            [field]: isArray(filters.value[field])
                ? filters.value[field]?.filter((v) => v !== value) || []
                : null,
        }
    }

type UseFilterReturn<T> = {
    filter: Ref<T>
    variables: ComputedRef<T>
    updateFilter: (val: T) => void
    updatePending: ComputedRef<boolean>
}

export function useFilter<T>(
    data: T,
    delay = 500,
    beforeUpdate?: () => void
): UseFilterReturn<T> {
    const filter: Ref<T> = ref(cloneDeep(data)) as Ref<T>
    const vars = ref(cloneDeep(data)) as Ref<T>
    let isSilent = false
    const updatePending = ref(false)

    // -eslint-disable-next-line deprecation/deprecation
    const update = debounce(
        (data: T) => {
            beforeUpdate?.()
            vars.value = cloneDeep(data)
            updatePending.value = false
        },
        { waitMs: delay }
    ).call

    const updateFilter = (val: T) => {
        isSilent = true
        filter.value = val
    }

    watch(
        () => filter.value,
        (n: T) => {
            if (isSilent) {
                isSilent = false
                return
            }

            update(n)
            updatePending.value = true
        },
        { deep: true }
    )

    const variables = computed(() => vars.value)

    return {
        filter,
        variables,
        updateFilter,
        updatePending: computed(() => updatePending.value),
    }
}
